/* eslint-disable no-undef */
// Importing Redux/Third party Library components
import { all, call, put, select, takeLatest } from 'redux-saga/effects'
import createRequestSaga from 'portal-modules/common/redux/create-request-saga'
import createFormAction from 'portal-modules/common/redux/create-form-action'
// Importing API's
import {
  refreshPlaidPaymentAccounts,
  getPaymentMethodWithSettings,
  getMoneyGram,
  deletePaymentAccount,
  addMoneyGramAccount,
  savePaymentMethod,
  updatePaymentAccount,
  saveCustomerPlaidItem,
  getPlaidInactiveAccounts,
  addBulkPaymentMoneyGramAccount,
  getAvailablePaymentTypes,
  createSetupIntent,
  getAvailablePaymentMethodCount,
} from '../../api/api'
// Importing constant
import {
  CUSTOM_JWT_STORAGE_KEY,
  JWT_STORAGE_KEY,
} from 'portal-modules/common/constants/keys'
import {
  PAYMENT_TYPES,
} from 'portal-modules/payments/constants'
import {
  COLLEEN_PAYMENT_LINK_ID
} from 'portal-modules/common/constants/keys'
// Importing Utility functions
import { localStorage as local } from '@entrata/utils'
import { isApplePayAvailable } from 'portal-modules/common/utils/apple-pay-utils'
import { setSessionCookies } from 'portal-utils/session-cookies'
import getProjectName from 'portal-modules/common/utils/get-project-name'
// Importing Redux Modules
import { addSettings } from 'portal-modules/common/redux/modules/settings'
import { LOGOUT } from 'portal-modules/auth/redux/modules/user'
import { addError } from 'portal-modules/common/redux/modules/errors'
import { postAutoPaymentDeleted } from './autoPayments'
import { deletedRefundMethod } from './refundMethod'
import { unsetLoading } from 'portal-modules/common/redux/modules/loading'
import { requestAlerts } from 'portal-modules/alerts-panel/redux/modules/alerts'
import { requestAutoPaymentStatus } from 'portal-modules/payments/redux/modules/autoPaymentStatus'
// Importing Utility Functions
import getPaymentSourceCondition from 'portal-modules/common/utils/get-payment-source-condition'
import { getDecryptedCryptoData } from 'portal-modules/common/utils/crypto-encryption'
// action types
const REQUEST_PAYMENT_METHODS = 'rp/paymentMethods/REQUEST_PAYMENT_METHODS'
const RECEIVE_PAYMENT_METHODS = 'rp/paymentMethods/RECEIVE_PAYMENT_METHODS'
const DELETE_PAYMENT_METHOD = 'rp/paymentMethods/DELETE_PAYMENT_METHOD'
const DELETE_PAYMENT_METHOD_FROM_UPDATE =
  'rp/paymentMethods/DELETE_PAYMENT_METHOD_FROM_UPDATE'
const PAYMENT_METHOD_DELETED = 'rp/paymentMethods/PAYMENT_METHOD_DELETED'
const ADD_PAYMENT_METHOD = 'rp/paymentMethods/ADD_PAYMENT_METHOD'
const PAYMENT_METHOD_ADDED = 'rp/paymentMethods/PAYMENT_METHOD_ADDED'
const UPDATE_PAYMENT_METHOD = 'rp/paymentMethods/UPDATE_PAYMENT_METHOD'
const PAYMENT_METHOD_UPDATED = 'rp/paymentMethods/PAYMENT_METHOD_UPDATED'
const CREATE_MONEYGRAM_ACCOUNT = 'rp/paymentMethods/CREATE_MONEYGRAM_ACCOUNT'
const MONEYGRAM_ACCOUNT_CREATED = 'rp/paymentMethods/MONEYGRAM_ACCOUNT_CREATED'
const SET_IS_SEPA_AVAILABLE = 'rp/paymentMethods/SET_IS_SEPA_AVAILABLE'
const DELETE_PAYMENT_METHOD_WITH_PAYMENT =
  'rp/paymentMethods/DELETE_PAYMENT_METHOD_WITH_PAYMENT'
const ADD_CUSTOMER_PLAID_ITEM = 'rp/paymentMethods/ADD_CUSTOMER_PLAID_ITEM'
const REQUEST_PLAID_INACTIVE_ACCOUNTS =
  'rp/paymentMethods/REQUEST_PLAID_INACTIVE_ACCOUNTS'
const RECEIVE_PLAID_INACTIVE_ACCOUNTS =
  'rp/paymentMethods/RECEIVE_PLAID_INACTIVE_ACCOUNTS'
const DELETE_PAYMENT_METHOD_WITH_REFUND_ACCOUNT =
  'rp/paymentMethods/DELETE_PAYMENT_METHOD_WITH_REFUND_ACCOUNT'
const DELETE_PAYMENT_METHOD_WITH_PAYMENTS_AND_REFUND_ACCOUNT =
  'rp/paymentMethods/DELETE_PAYMENT_METHOD_WITH_PAYMENTS_AND_REFUND_ACCOUNT'
const REFRESH_PLAID_PAYMENT_ACCOUNTS =
  'rp/paymentMethods/REFRESH_PLAID_PAYMENT_ACCOUNTS'
const REFRESHED_PLAID_PAYMENT_ACCOUNTS =
  'rp/paymentMethods/REFRESHED_PLAID_PAYMENT_ACCOUNTS'
const RESET_PAYMENT_METHODS = 'rp/paymentMethods/RESET_PAYMENT_METHODS'
const CREATE_SETUP_INTENT = 'rp/paymentMethods/CREATE_SETUP_INTENT'
const PAYMENT_METHOD_PLAID_UPDATED = 'rp/paymentMethods/PAYMENT_METHOD_PLAID_UPDATED'
const CLEAR_PLAID_UPDATED_FLAG = 'rp/paymentMethods/CLEAR_PLAID_UPDATED_FLAG'

const REQUEST_AVAILABLE_PAYMENT_METHOD_COUNT = 'rp/paymentMethods/REQUEST_AVAILABLE_PAYMENT_METHOD_COUNT'
const UPDATE_AVAILABLE_PAYMENT_METHOD_COUNT = 'rp/paymentMethods/UPDATE_AVAILABLE_PAYMENT_METHOD_COUNT'
export const MAX_PAYMENT_METHODS = 1000; // matches CPaymentMethodLimits::MAX_PAYMENT_METHODS

// Checking payment source condition for colleen payment
const { isColleenPayment } = getPaymentSourceCondition();

// reducer
export default (state = {}, { payload, type }) => {
  switch (type) {
    case RECEIVE_PAYMENT_METHODS:
      return {
        ...state,
        ...payload.paymentMethods,
        available_payment_method_count: payload?.settings?.available_payment_method_count ?? MAX_PAYMENT_METHODS
      };
    case SET_IS_SEPA_AVAILABLE:
      return { ...state, isSepaAvailable: payload }
    case RECEIVE_PLAID_INACTIVE_ACCOUNTS:
      return {
        ...state,
        ['plaid_inactive_accounts']: { ...payload },
      }
    case DELETE_PAYMENT_METHOD_FROM_UPDATE:
    case PAYMENT_METHOD_DELETED:
      return Object.values(state).reduce((obj, method) => {
        if (method.id === payload) return obj
        return { ...obj, [method.id]: method }
      }, {})
    case PAYMENT_METHOD_ADDED:
      return { ...state, [payload.id]: { ...payload } }
    case PAYMENT_METHOD_UPDATED:
      return {
        ...state,
        [payload.id]: { ...(state[payload.id] || {}), ...payload },
      }
    case PAYMENT_METHOD_PLAID_UPDATED:
      return {
        ...state,
        plaidMethodRecentlyUpdated: true
      }
    case CLEAR_PLAID_UPDATED_FLAG:
      return {
        ...state,
        plaidMethodRecentlyUpdated: false
      }
    case MONEYGRAM_ACCOUNT_CREATED:
      return Object.values(state).reduce((obj, method) => {
        if (
          method.id === 'moneygram' &&
          !method.hasAccount &&
          method.payment_type_id === 10
        ) {
          return { ...obj, [payload.id]: { ...payload, hasAccount: true } }
        } else {
          return { ...obj, [method.id]: method }
        }
      }, {})
    case REFRESHED_PLAID_PAYMENT_ACCOUNTS: {
      const tempState = { ...state }
      payload.forEach(account => {
        tempState[account.id] = account
      })
      return tempState
    }
    case RESET_PAYMENT_METHODS:
    case LOGOUT:
      return {}
    case UPDATE_AVAILABLE_PAYMENT_METHOD_COUNT:
      return {
        ...state,
        available_payment_method_count: typeof payload === 'number' ? payload : MAX_PAYMENT_METHODS
      }
    default:
      return state
  }
}

// action creators
export const requestPaymentMethods = payload => ({
  type: REQUEST_PAYMENT_METHODS,
  payload,
})

export const receivePaymentMethods = payload => ({
  type: RECEIVE_PAYMENT_METHODS,
  payload,
})

export const requestPlaidInactiveAccounts = payload => ({
  type: REQUEST_PLAID_INACTIVE_ACCOUNTS,
  payload,
})

export const receivePlaidInactiveAccounts = payload => ({
  type: RECEIVE_PLAID_INACTIVE_ACCOUNTS,
  payload,
})

export const deletePaymentMethod = (payload, callback) => ({
  type: DELETE_PAYMENT_METHOD,
  callback,
  payload,
})

export const deletePaymentMethodWithPayments = (payload, callback) => ({
  type: DELETE_PAYMENT_METHOD_WITH_PAYMENT,
  callback,
  payload,
})

export const createMoneyGramAccount = payload => ({
  type: CREATE_MONEYGRAM_ACCOUNT,
  payload,
})

export const addCustomerPlaidItem = payload => ({
  type: ADD_CUSTOMER_PLAID_ITEM,
  payload,
})

export const addPaymentMethod = createFormAction(ADD_PAYMENT_METHOD)
export const forceStorePaymentMethod = (payload, callback) => ({
  type: ADD_PAYMENT_METHOD,
  callback,
  payload,
})

export const updatePaymentMethod = (payload, callback) => ({
  type: UPDATE_PAYMENT_METHOD,
  callback,
  payload,
})

export const deletePaymentMethodWithRefundAccount = (payload, callback) => ({
  type: DELETE_PAYMENT_METHOD_WITH_REFUND_ACCOUNT,
  callback,
  payload,
})

export const setIsSepaAvailable = payload => ({
  type: SET_IS_SEPA_AVAILABLE,
  payload,
})

export const resetPaymentMethods = () => ({
  type: RESET_PAYMENT_METHODS,
})

// Action creator
export const requestSetupIntent = (payload, callback) => ({
  type: CREATE_SETUP_INTENT,
  callback,
  payload,
})

export const deletePaymentMethodWithPaymentsAndRefundAccount = (
  payload,
  callback,
) => ({
  type: DELETE_PAYMENT_METHOD_WITH_PAYMENTS_AND_REFUND_ACCOUNT,
  callback,
  payload,
})

export const refreshPaymentAccounts = (
  payload = [],
  callback = () => {},
  isBulkPayment = false,
) => ({
  type: REFRESH_PLAID_PAYMENT_ACCOUNTS,
  payload,
  callback,
  isBulkPayment,
})

// Add new action creators for Plaid update tracking
export const notifyPlaidMethodUpdated = () => ({
  type: PAYMENT_METHOD_PLAID_UPDATED
})

export const clearPlaidUpdatedFlag = () => ({
  type: CLEAR_PLAID_UPDATED_FLAG
})

export const updateAvailablePaymentMethodCount = (count) => ({
  type: UPDATE_AVAILABLE_PAYMENT_METHOD_COUNT,
  payload: count
})

export const requestAvailablePaymentMethodCount = () => ({
  type: REQUEST_AVAILABLE_PAYMENT_METHOD_COUNT
})

function* handlePaymentMethodError({ message, code, fields }, { key, action }) {
  message = fields && fields.length != 0 ? fields?.[0]?.message : message

  action.payload &&
    'function' === typeof action.payload.onError &&
    action.payload.onError()
  yield put(addError({ key, message }))
}

function* handlePlaidPaymentMethodError(
  { message, code, fields },
  { key, action },
) {
  message = fields && fields.length != 0 ? fields?.[0]?.message : message
  if (code == 1002 || code == 90034) {
    yield put(resetPaymentMethods())
    yield put(requestPaymentMethods())
    yield put(requestAlerts())
  }
  action.payload &&
    'function' === typeof action.payload.onError &&
    action.payload.onError()
  yield put(addError({ key, message }))
}

// sagas
function* fetchPaymentMethods({ payload }) {
  let moneygram_account = null
  let paymentMethods = null
  let applePayMethod = {}
  let availabelPaymentType = null
  let isInternational = false

  // Set extra parameters for API call, including language preference
  const extraParams = {
    language_preference: (
      (i18nUtils && i18nUtils.getLocale()) ||
      'en_US'
    ).replace('-', '_'),
    ...(isColleenPayment && { payment_link_id: parseInt(getDecryptedCryptoData(COLLEEN_PAYMENT_LINK_ID)) || 0})
  }

  // Check the project name and execute relevant API calls
  if ('rh' === getProjectName()) {
    // If the project name is 'rh', fetch payment methods with settings
    paymentMethods = yield call(getPaymentMethodWithSettings)
  } else {
    if (payload?.is_from_bulk_payment) {
      // If the payload indicates a bulk payment, fetch payment methods with settings
      ;[paymentMethods] = yield all([call(getPaymentMethodWithSettings)])
    } else {
      // Initialize the array with mandatory API calls
      const calls = [
        call(getPaymentMethodWithSettings, extraParams),
        call(getMoneyGram),
      ]

      // Check if Apple Pay is available and add the call to getAvailablePaymentTypes if true
      if (isApplePayAvailable()) {
        calls.push(
          call(getAvailablePaymentTypes, {
            amount: payload?.amount || 0,
            is_react_dev: process.env.NODE_ENV === 'development',
          }),
        )
      }

      // Execute all the API calls concurrently
      ;[paymentMethods, { moneygram_account }, applePayMethod] =
        yield all(calls)

      const {
        settings: { payments },
      } = yield select()
      isInternational = payments && payments.is_international

      if (isInternational) {
        [availabelPaymentType] = yield all([call(getAvailablePaymentTypes, {
          amount: payload?.amount,
          is_react_dev: process.env.NODE_ENV === 'development',
        })])
      }

      // Second call - with updated context if needed for international scenarios
      ;[paymentMethods, { moneygram_account }] = yield all([
        call(getPaymentMethodWithSettings, extraParams),
        call(getMoneyGram),
      ])
    }
  }
 
  const { settings, payment_accounts } = paymentMethods
  let allPaymentMethods = [...payment_accounts]

  // Filter the apple pay from the wallet settings object.
  let applePayDetails = applePayMethod?.wallet_settings?.find(
    wallet => wallet.name === 'apple_pay',
  )

  if (isApplePayAvailable() && applePayDetails?.is_enabled) {
    allPaymentMethods = [
      ...payment_accounts,
      {
        id: PAYMENT_TYPES.APPLE_PAY.value,
        payment_type_id: PAYMENT_TYPES.APPLE_PAY.value,
        ...applePayDetails,
      },
    ]
  }

  if( isInternational && availabelPaymentType?.available.length ) {
    availabelPaymentType?.available.map( paymentType => {
      if( paymentType.id == PAYMENT_TYPES.UNION_PAY.value ) {
        allPaymentMethods = [
          ...allPaymentMethods,
          {
            id: 'union_pay',
            payment_type_id: PAYMENT_TYPES.UNION_PAY.value
          },
        ]
      }
      if( paymentType.id == PAYMENT_TYPES.INSTANT_BANK_TRANSFER.value ) {
        allPaymentMethods = [
          ...allPaymentMethods,
          {
            id: 'instant_bank_transfer',
            payment_type_id: PAYMENT_TYPES.INSTANT_BANK_TRANSFER.value
          },
        ]
      }
    })
  }

  if (!payload?.is_from_bulk_payment) {
    if (moneygram_account) {
      allPaymentMethods = [
        ...allPaymentMethods,
        {
          ...moneygram_account,
          id: moneygram_account.moneygram_account_number,
          payment_type_id: 10,
          hasAccount: true,
        },
      ]
    } else {
      allPaymentMethods = [
        ...allPaymentMethods,
        {
          id: 'moneygram',
          payment_type_id: 10,
          hasAccount: false,
        },
      ]
    }
  }

  if (Array.isArray(allPaymentMethods)) {
    yield put(
      receivePaymentMethods({
        settings,
        paymentMethods: allPaymentMethods.reduce(
          (obj, method) => ({
            ...obj,
            [method.id]: method,
          }),
          {}
        )
      })
    )
  }

  yield put(addSettings({ paymentMethods: {...settings, ...(isColleenPayment && {passcode_details: paymentMethods.passcode})}}))
  yield payload?.callback &&
    typeof payload?.callback == 'function' &&
    payload?.callback()
}

function* postDeletePaymentMethod({ payload, callback }) {
  yield call(deletePaymentAccount, {
    payment_account_id: payload,
  })

  yield put({ type: PAYMENT_METHOD_DELETED, payload })
  yield put(deletedRefundMethod(payload))
  
  // get updated available payment method count
  yield put(requestAvailablePaymentMethodCount())
  
  const { autoPayments } = yield select()
  const connectedAutoPayments = Object.values(autoPayments).filter(
    ({ customer_payment_account_id }) => payload == customer_payment_account_id,
  )
  if (Object.keys(connectedAutoPayments).length) {
    yield all([
      ...Object.values(connectedAutoPayments).map(({ id }) =>
        put(postAutoPaymentDeleted(id)),
      ),
      put(requestAutoPaymentStatus())
    ]
    )
  }

  typeof callback == 'function' && callback()
}

function* postCreateMoneyGramAccount({ payload }) {
  let response
  if (payload) {
    const { token } = payload
    yield call(local.set, CUSTOM_JWT_STORAGE_KEY, token)
    response = yield call(addBulkPaymentMoneyGramAccount)
  } else {
    response = yield call(addMoneyGramAccount)
  }
  const { moneygram_account } = response
  if (moneygram_account) {
    let accountDetails = {
      ...moneygram_account,
      id: moneygram_account.moneygram_account_number,
      payment_type_id: 10,
    }

    yield put({
      type: MONEYGRAM_ACCOUNT_CREATED,
      payload: { ...accountDetails },
    })
  }
}

function* postAddPaymentMethod({ payload }) {
  let nextPayload = {}
  const {
    settings: { payments },
    userSettings,
    properties,
  } = yield select()
  const isUSClient = 'US' === properties[userSettings?.propertyId]?.country_code
  const isInternational = payments && payments.is_international
  const isAllowPlaid = payments && payments.plaid_account_verification

  const addedMethod = yield call(savePaymentMethod, payload)
  nextPayload = addedMethod

  /**
   * For international client, we will store the security code in storage
   * so that the user does not have to enter it again in the payment submission
   * workflow
   */
  if (isInternational && payload?.card_info && payload?.card_info.cvv) {
    addedMethod.card_info['cvv'] = payload?.card_info.cvv
  }

  yield put({
    type: PAYMENT_METHOD_ADDED,
    payload: addedMethod,
  })

  // get updated available payment method count
  yield put(requestAvailablePaymentMethodCount())

  localStorage.setItem('newPymentMethodAdded', 1)
  // get updated list of inactive accounts
  localStorage.setItem('newPaymentMethodAddedTime', new Date().getTime())
  if (addedMethod.payment_type_id == 4 && isUSClient && isAllowPlaid) {
    yield put(requestPlaidInactiveAccounts())
  }

  if (typeof payload.callback === 'function') {
    yield call(payload.callback, nextPayload)
  }
}

function* editPaymentMethod({ payload }) {
  const method = yield call(updatePaymentAccount, payload)

  yield put({
    type: PAYMENT_METHOD_UPDATED,
    payload: method,
  })

  if (payload.id !== method.id) {
    yield put({ type: DELETE_PAYMENT_METHOD_FROM_UPDATE, payload: payload.id })
  }
  typeof payload?.callback == 'function' && payload?.callback()
}

function* postAddCustomerPlaidItem({ payload }) {
  const { accounts } = yield call(saveCustomerPlaidItem, payload)

  if (accounts && accounts.length) {
    yield all(
      accounts.map(account =>
        put({
          type: PAYMENT_METHOD_ADDED,
          payload: account,
        }),
      ),
    )
  }

  // get updated available payment method count
  yield put(requestAvailablePaymentMethodCount())
  
  yield local.get('newLinkToken') && local.remove('newLinkToken')
  yield local.get('linkTokenExpiry') && local.remove('linkTokenExpiry')

  if (payload.is_relink_account && !payload.is_confirm_micro_deposit) {
    yield localStorage.removeItem('isRelinkAccount')
    yield localStorage.removeItem('relink_workflow')

    const redirectUrl =
      window?.location?.href?.toString()?.split('?')?.[0] ||
      window.location.href
    yield (window.location.href = redirectUrl)
  }

  // get updated list of inactive accounts always after saving customer plaid accounts
  yield put(requestPlaidInactiveAccounts())

  yield local.get('isPlaidLinkRefresh') && local.remove('isPlaidLinkRefresh')

  if (typeof payload.callback === 'function') {
    yield call(payload.callback, accounts)
  }
}

function* postRefreshPaymentAccounts({ payload, isBulkPayment = false }) {
  let callAPI = true
  if (local.get('refreshPaymentAccountApiTime')) {
    const previousTime = local.get('refreshPaymentAccountApiTime')
    const currentTime = new Date().getTime()

    if (currentTime - previousTime < 900000) {
      callAPI = false
    }
  }

  if (callAPI) {
    local.set('refreshPaymentAccountApiTime', new Date().getTime())

    let oldToken
    if (isBulkPayment) {
      const { user } = yield select()
      const rp_at = user?.token
      oldToken = rp_at

      const { leases } = yield select()
      const newToken = leases && Object.values(leases)[0]?.token

      if (newToken) {
        setSessionCookies(JWT_STORAGE_KEY, newToken)
      }
    }

    const paymentAccounts = yield call(refreshPlaidPaymentAccounts, {
      payment_account_ids: payload,
    })

    if (isBulkPayment && oldToken) {
      setSessionCookies(JWT_STORAGE_KEY, oldToken)
    }

    if (paymentAccounts?.length) {
      yield put({ type: REFRESHED_PLAID_PAYMENT_ACCOUNTS, payload })
    }
  }
}

function* postCreateSetupIntent({ payload, callback }) {
  const response = yield call(createSetupIntent, payload)
  yield typeof callback === 'function' && callback(response)
}

function* fetchPlaidInactiveAccounts({ payload }) {
  try {
    const { plaid_inactive_accounts, nonce } = yield call(
      getPlaidInactiveAccounts,
      { website_url: window.location.href },
    )
    localStorage.setItem('nonce', nonce)
    if (plaid_inactive_accounts && plaid_inactive_accounts.length) {
      yield put(unsetLoading({ key: 'global' }))
    }
    yield put(receivePlaidInactiveAccounts(plaid_inactive_accounts))
  } catch (error) {
    console.log('Error in Plaid Inactive Account =>', error)
  } finally {
    yield payload &&
      payload?.callback &&
      'function' === typeof payload.callback &&
      payload.callback()
  }
}

function* watchAddPaymentMethod() {
  yield takeLatest(
    ADD_PAYMENT_METHOD,
    createRequestSaga(postAddPaymentMethod, {
      key: 'paymentMethods',
      message: __('Adding new payment method'),
      successMessage: __('Payment method added'),
      write: true,
      onError: handlePaymentMethodError,
    }),
  )
}

function* watchUpdatePaymentMethod() {
  yield takeLatest(
    UPDATE_PAYMENT_METHOD,
    createRequestSaga(editPaymentMethod, {
      key: 'paymentMethods',
      message: __('Syncing updates'),
      successMessage: __('Payment method updated'),
      write: true,
      onError: handlePaymentMethodError,
    }),
  )
}

function* watchRequestPaymentMethods() {
  yield takeLatest(
    REQUEST_PAYMENT_METHODS,
    createRequestSaga(fetchPaymentMethods, {
      key: 'requestPaymentMethods',
      message: __('Finding payment methods'),
    }),
  )
}

function* watchDeletePaymentMethod() {
  yield takeLatest(
    DELETE_PAYMENT_METHOD,
    createRequestSaga(postDeletePaymentMethod, {
      key: 'paymentMethods',
      message: __('Syncing updates'),
      successMessage: __('Payment method deleted'),
      write: true,
    }),
  )
}

function* watchDeletePaymentMethodWithPayments() {
  yield takeLatest(
    DELETE_PAYMENT_METHOD_WITH_PAYMENT,
    createRequestSaga(postDeletePaymentMethod, {
      key: 'paymentMethods',
      message: __('Deleting Payment Method'),
      successMessage: __(
        'The auto-payment(s) and card associated have been deleted.',
      ),
      write: true,
    }),
  )
}

function* watchCreateMoneyGramAccount() {
  yield takeLatest(
    CREATE_MONEYGRAM_ACCOUNT,
    createRequestSaga(postCreateMoneyGramAccount, {
      key: 'payments',
      message: __('Creating account'),
      successMessage: __('MoneyGram account created.'),
      write: true,
    }),
  )
}

function* watchAddCustomerPlaidItemRequest() {
  yield takeLatest(
    ADD_CUSTOMER_PLAID_ITEM,
    createRequestSaga(postAddCustomerPlaidItem, {
      key: 'paymentMethods',
      messgae: __('Syncing updates'),
      successMessage: __('Payment method added'),
      duration: 500,
      write: true,
      onError: handlePlaidPaymentMethodError,
    }),
  )
}

function* watchRequestPlaidInactiveAccounts() {
  yield takeLatest(
    REQUEST_PLAID_INACTIVE_ACCOUNTS,
    createRequestSaga(fetchPlaidInactiveAccounts, {
      key: 'paymentMethods',
      message: __('Retrieving bank account options'),
    }),
  )
}

function* watchDeletePaymentMethodWithRefundAccount() {
  yield takeLatest(
    DELETE_PAYMENT_METHOD_WITH_REFUND_ACCOUNT,
    createRequestSaga(postDeletePaymentMethod, {
      key: 'paymentMethods',
      message: __('Deleting Payment Method'),
      successMessage: __(
        'The refund method and card associated have been deleted.',
      ),
      write: true,
    }),
  )
}

function* watchDeletePaymentMethodWithPaymentsAndRefundAccount() {
  yield takeLatest(
    DELETE_PAYMENT_METHOD_WITH_PAYMENTS_AND_REFUND_ACCOUNT,
    createRequestSaga(postDeletePaymentMethod, {
      key: 'paymentMethods',
      message: __('Deleting Payment Method'),
      successMessage: __(
        'The auto-payment(s), refund method and card associated have been deleted.',
      ),
      write: true,
    }),
  )
}

function* watchRefreshPaymentAccounts() {
  yield takeLatest(
    REFRESH_PLAID_PAYMENT_ACCOUNTS,
    createRequestSaga(postRefreshPaymentAccounts, {
      key: 'refreshPaymentMethods',
      message: __('Refreshing'),
      successMessage: __('Refreshed'),
      write: true,
    }),
  )
}

function* watchCreateSetupIntent() {
  yield takeLatest(
    CREATE_SETUP_INTENT,
    createRequestSaga(postCreateSetupIntent, {
      key: 'paymentMethods',
      message: __('Loading account creation form'),
      successMessage: __('Payment method added'),
      write: true,
      onError: handlePaymentMethodError,
    }),
  )
}

function* fetchAvailablePaymentMethodCount() {
  try {
    const response = yield call(getAvailablePaymentMethodCount);
    if( response?.available_payment_method_count !== null ) {
      yield put(updateAvailablePaymentMethodCount(response.available_payment_method_count));
    }
  } catch (error) {
    console.log('Error fetching available payment method count:', error);
  }
}

function* watchRequestAvailablePaymentMethodCount() {
  yield takeLatest(REQUEST_AVAILABLE_PAYMENT_METHOD_COUNT, fetchAvailablePaymentMethodCount)
}

export const sagas = [
  watchRequestPaymentMethods,
  watchAddPaymentMethod,
  watchDeletePaymentMethod,
  watchUpdatePaymentMethod,
  watchCreateMoneyGramAccount,
  watchDeletePaymentMethodWithPayments,
  watchAddCustomerPlaidItemRequest,
  watchRequestPlaidInactiveAccounts,
  watchDeletePaymentMethodWithRefundAccount,
  watchDeletePaymentMethodWithPaymentsAndRefundAccount,
  watchRefreshPaymentAccounts,
  watchCreateSetupIntent,
  watchRequestAvailablePaymentMethodCount
]
